import Streaming from './Streaming';
import StreamingError, { StreamingErrorCodes } from './StreamingError';
import { DoryStreamingMessage } from './StreamingMessages';

export default abstract class StreamingService<T> {
  /**
   * @hidden
   */
  public readonly SERVICENAME: string;
  /**
   * @hidden
   */
  public readonly NAMESPACE: T;
  protected readonly SOCKET: Streaming;

  public abstract get isAvailable(): boolean;
  public abstract set isAvailable(val: boolean);

  /**
   * @hidden
   */
  constructor(namespace: T, servicename: string, socket: Streaming) {
    this.NAMESPACE = namespace;
    this.SERVICENAME = servicename;
    this.SOCKET = socket;
  }

  /**
   * @hidden
   */
  protected RaiseNotConnectedError<T>(
    source: string,
    cb: (data: T, err: StreamingError) => void
  ): void {
    if (cb) {
      cb(
        undefined,
        new StreamingError(
          source,
          StreamingErrorCodes.NOT_CONNECTED,
          `Can't send message, not connected to WebSocket Server!`
        )
      );
    }
  }

  /**
   * @hidden
   */
  protected RaiseServiceUnavailableError<T>(
    source: string,
    cb: (data: T, err: StreamingError) => void
  ): void {
    if (cb) {
      cb(
        undefined,
        new StreamingError(
          source,
          StreamingErrorCodes.SERVICE_UNAVAILABLE,
          `Can't send message, requested service is currently not available`
        )
      );
    }
  }

  /**
   * @hidden
   */
  protected setTimeout(
    source: string,
    ms: number,
    cb: (data: DoryStreamingMessage, err?: StreamingError) => void
  ): (data: DoryStreamingMessage, err?: StreamingError) => void {
    const callback = (
      data: DoryStreamingMessage,
      err?: StreamingError
    ): void => {
      window.clearTimeout(handle);
      cb(data, err);
    };
    const handle = window.setTimeout(() => {
      cb(
        undefined,
        new StreamingError(
          source,
          StreamingErrorCodes.TIMEOUT,
          'Request to WebSocket timed out!'
        )
      );
    }, ms);

    return callback;
  }

  /**
   * @hidden
   */
  protected raiseDefaultErrors<T>(
    source: string,
    callback: (data: T, err?: StreamingError) => void,
    missingParams?: string
  ): boolean {
    if (!this.SOCKET.isConnected) {
      this.RaiseNotConnectedError(source, callback);
      return true;
    } else if (!this.isAvailable) {
      this.RaiseServiceUnavailableError(source, callback);
      return true;
    } else if (missingParams) {
      throw new StreamingError(
        source,
        StreamingErrorCodes.MISSING_PARAMETERS,
        missingParams
      );
    }
  }

  /**
   * @hidden
   */
  protected getSubscribeErrorHandler<T>(
    source: string,
    callback: (data: T, err?: StreamingError) => void,
    id: string,
    isSubscribed: boolean,
    missingParams?: string
  ) {
    const isDefaultError = this.raiseDefaultErrors(
      source,
      callback,
      missingParams
    );
    if (isDefaultError) return true;

    if (isSubscribed) {
      const [, service] = source.split('.');
      /*
       Condition for ALERT_STREAM -> if 'id && subscribed' is undefined, condition switch to 'ALERT_STREAM'
       ALERT_STREAM doesn't have a subscription id. hence the message `Can't subscribe to Alert Streaming, already subscribed!`
      */
      callback(
        undefined,
        new StreamingError(
          source,
          id
            ? StreamingErrorCodes.INVALID_ID
            : StreamingErrorCodes.INVALID_REQUEST,
          id
            ? `ID must be unique, ID ${id} is already in use.`
            : `Can't subscribe to ${service} Streaming, already subscribed!`
        )
      );
    }
  }
}
